import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ROLE_META, tenantColor } from "@/lib/meta";
import NotificationBell from "@/components/NotificationBell";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Briefcase,
  KanbanSquare,
  Users as UsersIcon,
  BarChart3,
  MapPin,
  Tags,
  Network,
  Activity,
  Building2,
  Package,
  LineChart,
  Settings,
  Layers,
  CalendarDays,
  Wrench,
  ChevronDown,
  ShieldCheck,
  LogOut,
  Menu,
  X,
  Image as ImageIcon,
} from "lucide-react";
import Security from "@/pages/Security";

export default function Layout({ children }) {
  const { user, logout, stopImpersonate, activeTenant, switchMyTenant } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [overdueJobs, setOverdueJobs] = useState(0);
  const [noticeDot, setNoticeDot] = useState(false);
  const [myTenants, setMyTenants] = useState([]);
  const [pendingTenant, setPendingTenant] = useState(null);
  const location = useLocation();
  const noticeSeenKey = `jobq:noticeSeen:${user.id}`;

  const isSuper = user.role === "superadmin";
  const canSeeHire = ["admin", "manager", "hire_manager", "hire_coordinator"].includes(user.role) || (isSuper && activeTenant);
  const brandLogoUrl = (() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (isSuper && activeTenant) {
      const t = tenants.find((x) => x.id === activeTenant);
      if (t?.has_logo) return `${backend}/api/tenants/${activeTenant}/logo?v=${t.logo_version || 0}`;
    } else if (user.tenant_id && user.tenant_has_logo) {
      return `${backend}/api/tenants/${user.tenant_id}/logo?v=${user.tenant_logo_version || 0}`;
    }
    return null;
  })();

  useEffect(() => {
    if (isSuper) api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
  }, [isSuper]);

  // Any user who belongs to more than one tenant gets an in-app tenant switcher.
  useEffect(() => {
    if (!isSuper && !user.impersonated_by) {
      api.get("/auth/my-tenants").then((r) => setMyTenants(r.data)).catch(() => {});
    } else {
      setMyTenants([]);
    }
  }, [isSuper, user.impersonated_by, user.id]);

  const onSwitchMyTenant = async (userId) => {
    if (!userId || userId === user.id) return;
    try {
      const result = await switchMyTenant(userId);
      if (result?.twofa_required) {
        toast.info("That account has two-factor enabled — please sign in with 2FA");
        await logout();
        navigate("/login");
        return;
      }
      navigate("/");
      window.location.reload();
    } catch {
      toast.error("Could not switch tenant");
    }
  };

  // Name of the tenant currently being viewed (for the header chip).
  const tenantLabel = isSuper
    ? (activeTenant ? (tenants.find((t) => t.id === activeTenant)?.name || user.tenant_name || "—") : "Platform")
    : (user.tenant_name || myTenants.find((t) => t.current)?.tenant_name || null);
  const tenantColorValue = isSuper
    ? (activeTenant ? (tenants.find((t) => t.id === activeTenant)?.color || tenantColor(activeTenant)) : tenantColor("platform"))
    : (user.tenant_color || tenantColor(user.tenant_id));
  // Only show the top-bar tenant tag for super admins and users who belong to 2+ tenants.
  const showTenantChip = tenantLabel && (isSuper || myTenants.length > 1);

  useEffect(() => {
    if (canSeeHire) {
      api.get("/hire/summary").then((r) => setOverdueCount(r.data.overdue_count || 0)).catch(() => {});
    } else {
      setOverdueCount(0);
    }
  }, [canSeeHire]);

  // Overdue jobs count for the Board link (role-scoped by the backend).
  const canSeeBoard =
    ["admin", "manager", "rigger", "job_dispatcher", "viewer"].includes(user.role) ||
    (isSuper && activeTenant);
  useEffect(() => {
    if (canSeeBoard) {
      api
        .get("/dashboard/stats")
        .then((r) => {
          setOverdueJobs(r.data.overdue_jobs || 0);
          const latest = r.data.latest_notice_at;
          const seen = localStorage.getItem(noticeSeenKey);
          setNoticeDot(!!latest && (!seen || latest > seen));
        })
        .catch(() => {});
    } else {
      setOverdueJobs(0);
      setNoticeDot(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeeBoard, activeTenant]);

  // Clear the "new notice" dot once the user opens the Home board
  useEffect(() => {
    if (location.pathname === "/board") {
      localStorage.setItem(noticeSeenKey, new Date().toISOString());
      setNoticeDot(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const applyVisibility = (n) => {
    if (isSuper) {
      if (n.roles?.includes("superadmin")) return true; // Tenants console always visible
      return !!activeTenant; // full admin nav appears once a tenant is selected
    }
    if (user.role === "hire_manager" || user.role === "hire_coordinator") {
      return ["/equipment", "/hire-dashboard", "/schedule", "/service-jobs"].includes(n.to);
    }
    if (user.role === "service_technician") {
      return ["/schedule", "/service-jobs"].includes(n.to);
    }
    if (user.role === "rigger" && user.dual_role && ["/schedule", "/service-jobs"].includes(n.to)) return true;
    if (user.role === "job_dispatcher") {
      return n.to === "/" || n.to === "/jobs" || n.to === "/board";
    }
    return !n.roles || n.roles.includes(user.role);
  };

  const nav = [
    { to: "/tenants", label: "Tenants", icon: Building2, testid: "nav-tenants", roles: ["superadmin"] },
    { to: "/admin/users", label: "User Administration", icon: UsersIcon, testid: "nav-admin-users", roles: ["superadmin"] },
    { to: "/board", label: "Home", icon: KanbanSquare, testid: "nav-board" },
    { to: "/", label: "Job Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/jobs", label: "Jobs", icon: Briefcase, testid: "nav-jobs" },
    { to: "/reports", label: "Job Reports", icon: BarChart3, testid: "nav-reports", roles: ["admin", "manager"] },
  ].filter(applyVisibility);

  const advancedNav = [
    { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule", roles: ["admin", "manager"] },
    { to: "/service-jobs", label: "Service Jobs", icon: Wrench, testid: "nav-service-jobs", roles: ["admin", "manager"] },
    { to: "/hire-dashboard", label: "Hire Dashboard", icon: LineChart, testid: "nav-hire-dashboard", roles: ["admin", "manager"] },
    { to: "/equipment", label: "Equipment Hire", icon: Package, testid: "nav-equipment", roles: ["admin", "manager"] },
  ].filter(applyVisibility);

  const settingsNav = [
    { to: "/users", label: "Users", icon: UsersIcon, testid: "nav-users", roles: ["admin", "manager"] },
    { to: "/locations", label: "Location Management", icon: MapPin, testid: "nav-locations", roles: ["admin"] },
    { to: "/departments", label: "Department Management", icon: Network, testid: "nav-departments", roles: ["admin"] },
    { to: "/job-statuses", label: "Job Status Management", icon: Tags, testid: "nav-job-statuses", roles: ["admin"] },
    { to: "/branding", label: "Branding", icon: ImageIcon, testid: "nav-branding", roles: ["admin"] },
    { to: "/activity", label: "Activity Log", icon: Activity, testid: "nav-activity", roles: ["admin"] },
  ].filter(applyVisibility);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleStopImpersonate = async () => {
    await stopImpersonate();
    toast.success("Returned to super admin");
    navigate("/tenants");
  };

  const role = ROLE_META[user.role];

  // Mandatory 2FA: admins/super admins without 2FA must set it up before using the app.
  if (user.twofa_setup_required && !user.impersonated_by) {
    return (
      <div className="min-h-screen bg-brand-warm flex flex-col items-center justify-center p-6" data-testid="twofa-gate">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo.png" alt="Job Q" className="h-10 w-10 rounded-md bg-white p-1 object-contain" />
          <span className="text-2xl font-thin tracking-[0.35em] uppercase text-zinc-900">Job Q</span>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-6 max-w-lg w-full mb-4">
          <h1 className="font-heading text-2xl font-black tracking-tighter text-zinc-900">Secure your account</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Two-factor authentication is required for your role. Set it up now to continue.
          </p>
        </div>
        <Security forced />
        <button onClick={handleLogout} className="mt-4 text-sm font-bold text-zinc-500 hover:text-zinc-900" data-testid="twofa-gate-logout">
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex bg-brand-warm text-zinc-900 ${user.impersonated_by ? "pt-9" : ""}`}>
      {user.impersonated_by && (
        <div className="fixed top-0 inset-x-0 z-50 bg-fuchsia-700 text-white text-sm font-semibold px-4 py-2 flex items-center justify-center gap-4" data-testid="impersonation-banner">
          <span>Viewing as {user.name}{user.tenant_name ? ` · ${user.tenant_name}` : ""}</span>
          <button onClick={handleStopImpersonate} data-testid="stop-impersonate-button"
            className="underline underline-offset-2 hover:no-underline font-bold">
            Return to Super Admin
          </button>
        </div>
      )}
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 lg:h-screen z-40 h-full w-64 bg-brand-ink text-zinc-100 flex flex-col overflow-y-auto transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-zinc-800">
          <div className="flex items-center justify-center gap-3">
            <img src={brandLogoUrl || "/logo.png"} alt={user.tenant_name || "Job Q"}
                 data-testid="sidebar-brand-logo"
                 className="h-10 w-10 rounded-md bg-white p-1 object-contain" />
            <div className="text-2xl font-thin tracking-[0.35em] uppercase leading-none text-white">
              Job Q
            </div>
          </div>
        </div>

        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
          {nav.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-semibold tracking-tight transition-colors ${
                  isActive
                    ? "bg-white text-zinc-950"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <Icon size={18} strokeWidth={2.2} />
              <span className="flex-1 flex items-center gap-1.5">
                {label}
                {to === "/board" && noticeDot && (
                  <span
                    data-testid="board-notice-dot"
                    title="New notice posted"
                    className="h-2 w-2 rounded-full bg-brand animate-pulse"
                  />
                )}
              </span>
              {to === "/board" && overdueJobs > 0 && (
                <span
                  data-testid="board-overdue-badge"
                  className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center"
                  title={`${overdueJobs} overdue job${overdueJobs > 1 ? "s" : ""}`}
                >
                  {overdueJobs}
                </span>
              )}
            </NavLink>
          ))}

          {advancedNav.length > 0 && (
            <div className={nav.length > 0 ? "pt-4 mt-3 border-t border-zinc-800" : ""}>
              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                data-testid="nav-advanced-toggle"
                aria-expanded={advancedOpen}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold tracking-tight text-zinc-400 hover:bg-zinc-900 hover:text-white transition-colors"
              >
                <Layers size={18} strokeWidth={2.2} />
                <span className="flex-1 text-left">Advanced Services</span>
                <ChevronDown size={16} className={`transition-transform duration-200 ${advancedOpen ? "rotate-180" : ""}`} />
              </button>
              {advancedOpen && (
                <div className="mt-1 space-y-1" data-testid="nav-advanced-group">
                  {advancedNav.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                      key={to}
                      to={to}
                      data-testid={testid}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 pl-9 pr-4 py-2.5 text-sm font-semibold tracking-tight transition-colors ${
                          isActive ? "bg-white text-zinc-950" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                        }`
                      }
                    >
                      <Icon size={16} strokeWidth={2.2} />
                      <span className="flex-1">{label}</span>
                      {to === "/hire-dashboard" && overdueCount > 0 && (
                        <span
                          data-testid="hire-overdue-badge"
                          className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center"
                          title={`${overdueCount} overdue hire${overdueCount > 1 ? "s" : ""}`}
                        >
                          {overdueCount}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {settingsNav.length > 0 && (
            <div className="pt-4 mt-3 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                data-testid="nav-settings-toggle"
                aria-expanded={settingsOpen}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold tracking-tight text-zinc-400 hover:bg-zinc-900 hover:text-white transition-colors"
              >
                <Settings size={18} strokeWidth={2.2} />
                <span className="flex-1 text-left">Settings</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform duration-200 ${settingsOpen ? "rotate-180" : ""}`}
                />
              </button>
              {settingsOpen && (
                <div className="mt-1 space-y-1" data-testid="nav-settings-group">
                  {settingsNav.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                      key={to}
                      to={to}
                      data-testid={testid}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 pl-9 pr-4 py-2.5 text-sm font-semibold tracking-tight transition-colors ${
                          isActive
                            ? "bg-white text-zinc-950"
                            : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                        }`
                      }
                    >
                      <Icon size={16} strokeWidth={2.2} />
                      <span className="flex-1">{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 bg-zinc-800 flex items-center justify-center font-bold text-sm">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="current-user-name">
                {user.name}
              </div>
              <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${role.cls}`}>
                {role.label}{user.dual_role ? " + Service Tech" : ""}
              </span>
            </div>
          </div>
          {!isSuper && myTenants.length > 1 && (
            <div className="mb-3" data-testid="my-tenant-switcher-wrap">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1.5">
                Your Tenant
              </div>
              <div className="relative">
                <span
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full pointer-events-none z-10"
                  style={{ backgroundColor: user.tenant_color || tenantColor(user.tenant_id) }}
                />
                <select
                  value={user.id}
                  onChange={(e) => {
                    const uid = e.target.value;
                    if (uid === user.id) return;
                    const t = myTenants.find((x) => x.user_id === uid);
                    setPendingTenant({ userId: uid, name: t?.tenant_name || "this tenant", role: t?.role });
                  }}
                  data-testid="my-tenant-switcher"
                  className="w-full bg-zinc-900 border border-zinc-700 text-sm font-semibold text-zinc-100 pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  {myTenants.map((t) => (
                    <option key={t.user_id} value={t.user_id}>{t.tenant_name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <NavLink
            to="/security"
            onClick={() => setOpen(false)}
            data-testid="nav-security"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 mb-2 text-sm font-bold text-zinc-300 border border-zinc-700 hover:bg-white hover:text-zinc-950 transition-colors"
          >
            <ShieldCheck size={16} /> Security & 2FA
          </NavLink>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-zinc-300 border border-zinc-700 hover:bg-white hover:text-zinc-950 transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-brand-ink text-white">
          <span className="flex items-center gap-2 min-w-0">
            <img src="/logo.png" alt="Job Q" className="h-7 w-7 rounded bg-white p-0.5 object-contain" />
            {showTenantChip ? (
              <span className="flex flex-col leading-tight min-w-0" data-testid="active-tenant-chip-mobile">
                <span className="text-sm font-thin tracking-[0.25em] uppercase text-white">Job Q</span>
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: tenantColorValue }} />
                  <span className="text-[10px] uppercase tracking-wider font-bold text-white/70 truncate">{tenantLabel}</span>
                </span>
              </span>
            ) : (
              <span className="text-lg font-thin tracking-[0.3em] uppercase text-white">Job Q</span>
            )}
          </span>
          <div className="flex items-center gap-1">
            <div className="text-white"><NotificationBell /></div>
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </header>
        <div className="hidden lg:flex items-center justify-between px-6 py-2.5 border-b border-zinc-200 bg-white">
          {showTenantChip ? (
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-ink text-white"
              data-testid="active-tenant-chip"
            >
              <span
                className="w-2.5 h-2.5 rounded-full ring-2 ring-white/20"
                style={{ backgroundColor: tenantColorValue }}
                data-testid="active-tenant-dot"
              />
              <span className="text-[11px] uppercase tracking-[0.15em] font-bold" data-testid="active-tenant-name">
                {tenantLabel}
              </span>
            </div>
          ) : <span />}
          <NotificationBell />
        </div>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      <AlertDialog open={!!pendingTenant} onOpenChange={(o) => { if (!o) setPendingTenant(null); }}>
        <AlertDialogContent data-testid="switch-tenant-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch tenant?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll leave <b>{user.tenant_name || "your current tenant"}</b> and load the workspace for{" "}
              <b>{pendingTenant?.name}</b>
              {pendingTenant?.role ? <> as <b className="capitalize">{pendingTenant.role.replace("_", " ")}</b></> : null}. Any unsaved work will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="switch-tenant-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="switch-tenant-confirm-btn"
              onClick={() => { const uid = pendingTenant?.userId; setPendingTenant(null); if (uid) onSwitchMyTenant(uid); }}
            >
              Switch tenant
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
